/**
 * Traduction des énumérations backend en libellé + variante de pastille.
 * Centralisé ici parce que la liste, la page de scan et le détail d'inventaire
 * affichaient les mêmes statuts avec trois mappings divergents.
 */

/**
 * Statut d'une ligne d'inventaire (`statut` de LigneInventaireDTO).
 *
 * `portee` est optionnelle et ne sert qu'à nommer précisément un HORS_PERIMETRE : sans elle on
 * reste sur un libellé générique, jamais sur « AUTRE IMPORT », qui serait faux sur un inventaire
 * par tissu.
 */
export const badgeLigne = (statut, portee) => {
  switch (statut) {
    case "OK":
      return { label: "OK", variant: "success" };
    // Rouleau réel mais étranger au périmètre : ni une erreur, ni un OK.
    case "HORS_PERIMETRE":
      return { label: libelleHorsPerimetre(portee), variant: "warn" };
    case "INCONNU":
      return { label: "INCONNU", variant: "info" };
    case "MANQUANT":
      return { label: "MANQUANT", variant: "danger" };
    case "ECART":
      return { label: "ECART", variant: "warn" };
    // Attendu = reste à scanner, pas une anomalie : neutre.
    case "ATTENDU":
      return { label: "A SCANNER", variant: "neutral" };
    default:
      return { label: statut || "", variant: "neutral" };
  }
};

/** Statut d'un inventaire (`statut` d'InventaireDTO). */
export const badgeInventaire = (statut) => {
  if (statut === "VALIDE") return { label: statut, variant: "success" };
  if (statut === "EN_COURS") return { label: statut, variant: "warn" };
  return { label: statut || "BROUILLON", variant: "neutral" };
};

/** Résultat d'un scan d'entrée (`scanStatus` d'AssetScanDTO). */
export const badgeScanEntree = (scanStatus) => {
  switch (scanStatus) {
    case "NEWLY_VALIDATED":
      return { label: "Validation effectuée avec succès", variant: "success" };
    case "ALREADY_VALIDATED":
      return { label: "Rouleau déjà validé", variant: "warn" };
    case "ALREADY_EXITED":
      return { label: "Rouleau déjà sorti du stock", variant: "danger" };
    default:
      return { label: scanStatus || "", variant: "neutral" };
  }
};

/**
 * Libellé du badge HORS_PERIMETRE selon la portée de l'inventaire. Le motif exact
 * (« Appartient à l'import X », « Appartient au tissu Y ») est de toute façon composé
 * côté serveur dans `commentaire` : ceci n'est que l'étiquette courte.
 */
const libelleHorsPerimetre = (portee) => {
  if (portee === "IMPORT") return "AUTRE IMPORT";
  if (portee === "TISSU") return "AUTRE TISSU";
  return "HORS PÉRIM.";
};

/**
 * Périmètre affiché dans la liste : « Global », « Import 9812 — TISSU » ou
 * « Tissu DENIM 12OZ ».
 *
 * ⚠️ Les trois portées sont testées explicitement. Un `if (portee !== "IMPORT") return "Global"`
 * affichait « Global » sur un inventaire par tissu — donc un périmètre faux, sans aucune erreur.
 */
export const perimetreCourt = (inv) => {
  if (!inv) return "";
  if (inv.portee === "IMPORT") {
    const tissu = inv.tissu ? ` — ${inv.tissu}` : "";
    return `Import ${inv.numeroImport || "?"}${tissu}`;
  }
  // « tous imports » distingue cette portée de la précédente, dont le libellé nomme AUSSI un tissu.
  if (inv.portee === "TISSU") return `Tissu ${inv.tissu || "?"} — tous imports`;
  return "Global";
};

/**
 * Périmètre affiché en sous-titre de l'écran de scan. L'opérateur doit savoir
 * s'il compte tout le stock, un import précis ou une référence tissu, sinon
 * « AUTRE IMPORT » / « AUTRE TISSU » n'a aucun sens pour lui.
 */
export const perimetreLong = (src) => {
  if (!src) return "";
  if (src.portee === "IMPORT") return `Import ${src.numeroImport || "?"}`;
  if (src.portee === "TISSU") return `Tissu ${src.tissu || "?"} (tous imports)`;
  return "Inventaire global";
};

export const formatDate = (date) =>
  date
    ? new Date(date).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";
